import React, { useMemo, useState } from "react";
import { Stethoscope, MapPin, Truck, Search, Crosshair } from "lucide-react";
import { READINESS_COLORS } from "@/pages/fleet/constants";

/**
 * Reusable directory of locatable fleet entities (patients, clinicians, vehicles).
 * Clicking an entry calls onSelect(kind, item). `kind` is one of
 * "patient" | "clinician" | "vehicle" to match FleetMap popup kinds.
 */
export default function FleetLocator({ data, onSelect }) {
  const [tab, setTab] = useState("patients");
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();

  const patients = data.patients || [];
  const clinicians = data.clinicians || [];
  const vehicles = data.vehicles || [];

  const match = (...fields) =>
    !query || fields.some((f) => (f || "").toString().toLowerCase().includes(query));

  const rows = useMemo(() => {
    if (tab === "patients") return patients.filter((p) => match(p.name, p.mrn, p.city));
    if (tab === "clinicians")
      return clinicians.filter((c) => match(c.name, c.role_code, c.facility_name));
    return vehicles.filter((v) => match(v.plate_number, v.vehicle_type, v.status));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, query, patients, clinicians, vehicles]);

  const tabs = [
    { key: "patients", label: "Patients", count: patients.length, Icon: MapPin },
    { key: "clinicians", label: "Clinicians", count: clinicians.length, Icon: Stethoscope },
    { key: "vehicles", label: "Vehicles", count: vehicles.length, Icon: Truck },
  ];

  const hasCoords = (it) => !!(it.latitude && it.longitude);

  return (
    <div data-testid="fleet-locator">
      <div className="flex items-center gap-2 mb-2">
        <Crosshair className="w-3.5 h-3.5 text-[#0B7E8C]" />
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7280]">
          Locate on Map
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-2 rounded-lg bg-[#F1F5F9] p-1">
        {tabs.map(({ key, label, count, Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            data-testid={`fleet-locator-tab-${key}`}
            className={`flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11px] font-medium transition-colors ${
              tab === key
                ? "bg-white text-[#0B7E8C] shadow-sm"
                : "text-[#6B7280] hover:text-[#1F2937]"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
            <span className="text-[10px] text-[#94A3B8]">({count})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-2">
        <Search className="w-3.5 h-3.5 text-[#94A3B8] absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${tab}…`}
          data-testid="fleet-locator-search"
          className="w-full text-xs pl-8 pr-2 py-2 rounded-md border border-[#E5E7EB] focus:outline-none focus:ring-2 focus:ring-[#0B7E8C]/30"
        />
      </div>

      {/* List */}
      <div className="space-y-1 max-h-[300px] overflow-auto pr-1">
        {rows.length === 0 && (
          <div className="text-[12px] text-[#94A3B8] italic py-4 text-center">No matches.</div>
        )}

        {tab === "patients" &&
          rows.map((p) => (
            <LocatorRow
              key={p.patient_id}
              testId={`fleet-locator-patient-${p.mrn}`}
              disabled={!hasCoords(p)}
              onClick={() => onSelect("patient", p)}
              icon={<MapPin className="w-3.5 h-3.5 text-white" />}
              iconBg={p.has_active_visit ? "#EF4444" : "#0B7E8C"}
              title={p.name || "Unnamed"}
              subtitle={`${p.mrn || "—"}${p.city ? " · " + p.city : ""}`}
            />
          ))}

        {tab === "clinicians" &&
          rows.map((c) => {
            const rc = READINESS_COLORS[c.readiness] || READINESS_COLORS.offline;
            return (
              <LocatorRow
                key={c.clinician_id}
                testId={`fleet-locator-clinician-${c.clinician_id}`}
                disabled={!hasCoords(c)}
                onClick={() => onSelect("clinician", c)}
                icon={<Stethoscope className="w-3.5 h-3.5 text-white" />}
                iconBg={rc.dot}
                title={c.name || "Clinician"}
                subtitle={`${(c.role_code || "").replace("_", " ")}${
                  c.facility_name ? " · " + c.facility_name : ""
                }`}
              />
            );
          })}

        {tab === "vehicles" &&
          rows.map((v) => (
            <LocatorRow
              key={v.id}
              testId={`fleet-locator-vehicle-${v.plate_number}`}
              disabled={!hasCoords(v)}
              onClick={() => onSelect("vehicle", v)}
              icon={<Truck className="w-3.5 h-3.5 text-white" />}
              iconBg="#475569"
              title={v.plate_number || "Vehicle"}
              subtitle={`${(v.vehicle_type || "").replace("_", " ")}${
                v.status ? " · " + v.status : ""
              }`}
            />
          ))}
      </div>
    </div>
  );
}

function LocatorRow({ icon, iconBg, title, subtitle, onClick, disabled, testId }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      data-testid={testId}
      className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:bg-[#EAF8FA] cursor-pointer"
      }`}
    >
      <span
        className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 shadow-sm"
        style={{ background: iconBg }}
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-medium text-[#1F2937] truncate">{title}</span>
        <span className="block text-[11px] text-[#6B7280] capitalize truncate">{subtitle}</span>
      </span>
      {disabled ? (
        <span className="text-[9px] uppercase font-semibold text-[#F59E0B] shrink-0">No location</span>
      ) : (
        <Crosshair className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" />
      )}
    </button>
  );
}
